import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BookOpen, Users, FileText, CheckCircle, ChevronRight } from "lucide-react";
import pnup from "../assets/pnup.png";
import trjt from "../assets/trjt.png";

const API = "https://simta-production.up.railway.app/api";

// const IMG = "https://images.unsplash.com/photo-1761492190275-129cf71ff124?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGlicmFyeSUyMG1vZGVybnxlbnwwfHx8fDE3NzgwNDU2NDZ8MA&ixlib=rb-4.1.0&q=85";

function ProgressMini({ value, max = 8 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} className={`h-2 flex-1 rounded-sm ${i < value ? "bg-[#002147]" : "bg-slate-200"}`} />
      ))}
    </div>
  );
}

export default function HomePage() {
  const [stats, setStats] = useState({ total_mahasiswa: 0, total_dosen: 0, total_bimbingan: 0, siap_ujian: 0 });
  const [mahasiswaList, setMahasiswaList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/stats`),
      axios.get(`${API}/public/mahasiswa`),
    ]).then(([sRes, mRes]) => {
      setStats(sRes.data);
      setMahasiswaList(mRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFDFD] font-[Manrope]">
      {/* Navbar */}
      <nav className="bg-[#002147] px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center overflow-hidden shrink-0">
            <img src={pnup} alt="PNUP Logo" className="w-full h-full object-contain"/>
          </div>
          <span className="text-white font-bold font-[Outfit]">SIMTA - Sistem Monitoring Tugas Akhir</span>
        </div>
        
      </nav>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <img
          src={trjt}
          alt="trjt"
          className="w-full md:h-[420px] h-[260px] object-contain bg-[#002147] object-center"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-[#002147]/70">
          {/* Center Content */}
          <div className="absolute inset-0 flex items-end justify-center pb-1 md:pb-4">
            <Link
              to="/login"
              data-testid="hero-login-btn"
              className="flex items-center gap-2 bg-white text-[#002147] hover:bg-slate-100 px-4 py-1 rounded-sm font-semibold transition-all shadow-md"
            >
              Masuk ke Sistem
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 justify-center">
        {/* <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4"> */}
          {[
            { label: "Total Mahasiswa", value: stats.total_mahasiswa, icon: <Users size={20} className="text-blue-500" /> },
            // { label: "Total Dosen", value: stats.total_dosen, icon: <Users size={20} className="text-green-500" /> },
            // { label: "Total Bimbingan", value: stats.total_bimbingan, icon: <FileText size={20} className="text-purple-500" /> },
            { label: "Siap Ujian", value: stats.siap_ujian, icon: <CheckCircle size={20} className="text-emerald-500" /> },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-3 p-4 bg-slate-50 rounded-sm border border-slate-100">
              {s.icon}
              <div>
                <p className="text-2xl font-bold text-[#002147] font-[Outfit]">{s.value}</p>
                <p className="text-xs text-[#475569] uppercase tracking-wide">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mahasiswa Table */}
      <div className="max-w-6xl mx-auto px-8 py-10">
        {/* <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A] font-[Outfit]">Daftar Mahasiswa</h2>
            <p className="text-[#475569] text-sm mt-1">Progress bimbingan tugas akhir seluruh mahasiswa</p>
          </div>
        </div> */}

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-[#002147] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-[#475569] text-sm">Memuat data...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="homepage-mahasiswa-table">
                <thead>

                    {/* Header utama */}
                    <tr className="bg-[#002147] text-white text-xs uppercase tracking-widest border-b-2 border-slate-400">
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        No
                      </th>
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        Nama Mahasiswa
                      </th>
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        NIM
                      </th>
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        Judul TA
                      </th>
                      <th
                        colSpan={2}className="px-4 py-3 text-left font-bold">
                        Pembimbing
                      </th>
                      <th
                        colSpan={2}className="px-4 py-3 text-left font-bold">
                        Pendamping
                      </th>
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        Status
                      </th>
                      <th rowSpan={2} className="px-4 py-3 text-left font-bold">
                        Update
                      </th>
                    </tr>
                    
                </thead>
                <tbody>
                  {mahasiswaList.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-[#475569] text-sm">
                        Belum ada data mahasiswa
                      </td>
                    </tr>
                  ) : (
                    mahasiswaList.map((m, i) => (
                      <tr key={m.id} data-testid={`homepage-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-100 transition-colors">
                        <td className="px-4 py-3 text-sm text-[#475569]">{i + 1}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-[#0F172A]">{m.name}</td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{m.nim || "-"}</td>
                        <td className="px-4 py-3 w-[320px]">
                          <p className="text-sm text-[#475569] break-words leading-relaxed" 
                          >{m.judul_ta || "-"}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{m.pembimbing1_name || "-"}</td>
                        <td className="px-4 py-3 w-32">
                          <ProgressMini value={m.p1_count} />
                          <p className="text-xs text-[#475569] mt-1">{m.p1_count}/8</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{m.pembimbing2_name || "-"}</td>
                        <td className="px-4 py-3 w-32">
                          <ProgressMini value={m.p2_count} />
                          <p className="text-xs text-[#475569] mt-1">{m.p2_count}/8</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-sm text-xs font-semibold whitespace-nowrap ${m.siap_ujian ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {m.siap_ujian ? "Siap Ujian" : "Belum Siap"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#475569] whitespace-nowrap">
                          {m.last_bimbingan || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 text-center text-sm text-[#475569]">
        SIMTA &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
